declare global {
  interface Window {
    __TAURI__?: {
      core?: {
        invoke: (cmd: string, args?: any) => Promise<any>;
      };
    };
    __TAURI_INTERNALS__?: any;
  }
}

export function isTauri(): boolean {
  return typeof window !== 'undefined' && 
    (!!window.__TAURI__ || !!window.__TAURI_INTERNALS__);
}

interface TauriFetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  timeout?: number;
}

export async function tauriFetch(url: string, options: TauriFetchOptions = {}): Promise<Response> {
  if (!isTauri()) {
    const fetchOptions: RequestInit = {
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body
    };
    
    if (options.timeout) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), options.timeout);
      fetchOptions.signal = controller.signal;
      
      try {
        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    }
    
    return fetch(url, fetchOptions);
  }
  
  try {
    const { fetch: tauriFetchFn } = await import('@tauri-apps/plugin-http');
    
    const response = await tauriFetchFn(url, {
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body || undefined,
      connectTimeout: options.timeout
    });
    
    return response;
  } catch (error: any) {
    if (error?.message?.includes('not found') || error?.message?.includes('plugin')) {
      console.warn('[TauriFetch] HTTP plugin not available, falling back to native fetch');
      const fetchOptions: RequestInit = {
        method: options.method || 'GET',
        headers: options.headers,
        body: options.body
      };
      return fetch(url, fetchOptions);
    }
    throw error;
  }
}

export async function tauriStreamFetch(url: string, options: TauriFetchOptions = {}): Promise<Response> {
  if (!isTauri()) {
    return fetch(url, {
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body
    });
  }
  
  try {
    const { fetch: tauriFetchFn } = await import('@tauri-apps/plugin-http');
    
    const response = await tauriFetchFn(url, {
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body || undefined
    });
    
    return response;
  } catch (error: any) {
    if (error?.message?.includes('not found') || error?.message?.includes('plugin')) {
      console.warn('[TauriStreamFetch] HTTP plugin not available, falling back to native fetch');
      return fetch(url, {
        method: options.method || 'GET',
        headers: options.headers,
        body: options.body
      });
    }
    throw error;
  }
}

export async function openExternalUrl(url: string): Promise<void> {
  if (!isTauri()) {
    window.open(url, '_blank');
    return;
  }
  
  try {
    const { open } = await import('@tauri-apps/plugin-shell');
    await open(url);
  } catch {
    window.open(url, '_blank');
  }
}

export async function blobToUint8Array(blob: Blob): Promise<Uint8Array> {
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
}

export async function fileToUint8Array(file: File): Promise<Uint8Array> {
  const buffer = await file.arrayBuffer();
  return new Uint8Array(buffer);
}

export interface MultipartBody {
  body: Uint8Array;
  contentType: string;
}

export function buildMultipartBody(
  metadata: object,
  fileData: Uint8Array,
  fileMimeType: string
): MultipartBody {
  const boundary = '-------314159265358979323846264338327950';
  const delimiter = `\r\n--${boundary}\r\n`;
  const closeDelimiter = `\r\n--${boundary}--`;
  
  const metadataJson = JSON.stringify(metadata);
  
  const metadataPart = 
    delimiter +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    metadataJson;
  
  const filePart = 
    delimiter +
    `Content-Type: ${fileMimeType}\r\n` +
    'Content-Transfer-Encoding: binary\r\n\r\n';
  
  const encoder = new TextEncoder();
  const metadataBytes = encoder.encode(metadataPart);
  const filePartHeaderBytes = encoder.encode(filePart);
  const closeDelimiterBytes = encoder.encode(closeDelimiter);
  
  const totalLength = metadataBytes.length + filePartHeaderBytes.length + fileData.length + closeDelimiterBytes.length;
  const body = new Uint8Array(totalLength);
  
  let offset = 0;
  body.set(metadataBytes, offset);
  offset += metadataBytes.length;
  body.set(filePartHeaderBytes, offset);
  offset += filePartHeaderBytes.length;
  body.set(fileData, offset);
  offset += fileData.length;
  body.set(closeDelimiterBytes, offset);
  
  return {
    body,
    contentType: `multipart/related; boundary=${boundary}`
  };
}

interface TauriFetchBinaryOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: Uint8Array;
  timeout?: number;
}

export async function tauriFetchBinary(url: string, options: TauriFetchBinaryOptions = {}): Promise<Response> {
  if (!isTauri()) {
    return fetch(url, {
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body
    });
  }
  
  try {
    const { fetch: tauriFetchFn } = await import('@tauri-apps/plugin-http');
    
    const response = await tauriFetchFn(url, {
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body,
      connectTimeout: options.timeout
    });
    
    return response;
  } catch (error: any) {
    if (error?.message?.includes('not found') || error?.message?.includes('plugin')) {
      console.warn('[TauriFetchBinary] HTTP plugin not available, falling back to native fetch');
      return fetch(url, {
        method: options.method || 'GET',
        headers: options.headers,
        body: options.body
      });
    }
    throw error;
  }
}
